/**
 * RateSettimanaleTab — Cash flow settimanale delle uscite programmate
 * dai bonifici delle rate fornitori (richieste accettate).
 * Include: selector orizzonte (4/8/12 settimane), grafico a barre,
 * click su barra → modal dettaglio rate.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, LabelList } from "recharts";
import { X, TrendingDown, Calendar, ExternalLink, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/api";

const eur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const eurFull = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const STATO_STYLE = {
  da_pagare: { label: "Da pagare", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  in_scadenza: { label: "In scadenza", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  scaduto: { label: "Scaduto", cls: "bg-rose-100 text-rose-800 border-rose-300" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function RateSettimanaleTab() {
  const [weeks, setWeeks] = React.useState(4);
  const [selectedBucket, setSelectedBucket] = React.useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["cashflow-rate-settimanale", weeks],
    queryFn: () =>
      api.get(`/costi-variabili/rate-fornitori/cashflow-settimanale?weeks=${weeks}`).then((r) => r.data),
  });

  const chartData = React.useMemo(() => {
    if (!data?.buckets) return [];
    return data.buckets.map((b) => ({
      name: b.week_label,
      valore: b.totale_ivato,
      imponibile: b.totale_imponibile,
      iva: b.totale_iva,
      count: b.count,
      is_current: b.is_current,
      bucket: b,
    }));
  }, [data]);

  const maxVal = chartData.reduce((m, x) => Math.max(m, x.valore || 0), 0);

  return (
    <div className="space-y-6" data-testid="rate-settimanale-tab">
      {/* Header + controlli */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Cash flow bonifici fornitori</div>
          <h2 className="font-display text-xl xl:text-2xl font-black mt-1">
            Uscite programmate settimanali
          </h2>
          <p className="text-slate-600 text-sm mt-1">
            Rate accettate ancora da pagare, aggregate per settimana lun-dom.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Orizzonte</label>
          <select
            value={weeks}
            onChange={(e) => setWeeks(Number(e.target.value))}
            data-testid="select-weeks"
            className="px-3 py-1.5 border border-slate-300 bg-white text-sm font-semibold rounded-sm"
          >
            <option value={4}>4 settimane</option>
            <option value={8}>8 settimane</option>
            <option value={12}>12 settimane</option>
          </select>
        </div>
      </div>

      {/* KPI riepilogo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Totale periodo (IVA)"
          value={eur(data?.totale_periodo_ivato || 0)}
          icon={TrendingDown}
          bad
          testId="kpi-tot-ivato"
        />
        <StatCard
          label="Totale imponibile"
          value={eur(data?.totale_periodo_imponibile || 0)}
          icon={Wallet}
          testId="kpi-tot-imp"
        />
        <StatCard label="Numero rate" value={data?.num_rate ?? 0} icon={Calendar} testId="kpi-num-rate" />
        <StatCard
          label={`Media/settimana`}
          value={eur((data?.totale_periodo_ivato || 0) / (weeks || 1))}
          icon={TrendingDown}
          testId="kpi-media-settimana"
        />
      </div>

      {/* Grafico */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Grafico settimanale</div>
            <h3 className="font-display font-bold text-lg">
              Bonifici da eseguire · prossime {weeks} settimane
            </h3>
            {data?.orizzonte_dal && (
              <p className="text-[11px] text-slate-500 mt-0.5">
                Dal <b>{fmtDate(data.orizzonte_dal)}</b> al <b>{fmtDate(data.orizzonte_al)}</b> · Clicca una barra per il dettaglio
              </p>
            )}
          </div>
        </div>
        <div className="w-full h-80">
          {isLoading ? (
            <div className="h-full flex items-center justify-center text-slate-400">Caricamento…</div>
          ) : chartData.every((c) => !c.valore) ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-500">
              <Wallet className="w-10 h-10 text-slate-300 mb-2" />
              <div className="text-sm">Nessuna rata fornitore da pagare nel periodo.</div>
              <div className="text-[11px] text-slate-400 mt-1">
                Le uscite compaiono qui quando accetti una Richiesta fornitore da un cantiere.
              </div>
            </div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 24, right: 12, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={weeks > 6 ? -20 : 0}
                  textAnchor={weeks > 6 ? "end" : "middle"}
                  height={weeks > 6 ? 60 : 30}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0].payload;
                    return (
                      <div className="bg-white p-3 border border-slate-300 shadow-lg rounded-sm text-xs">
                        <div className="font-bold text-slate-900 mb-1">
                          Settimana {p.name}
                          {p.is_current && <span className="ml-2 text-[10px] uppercase font-bold text-orange-600">In corso</span>}
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                          <div className="text-slate-500">Rate:</div>
                          <div className="font-bold text-right">{p.count}</div>
                          <div className="text-slate-500">Imponibile:</div>
                          <div className="font-mono text-right">{eurFull(p.imponibile)}</div>
                          <div className="text-slate-500">IVA:</div>
                          <div className="font-mono text-right">{eurFull(p.iva)}</div>
                          <div className="text-slate-700 font-bold">Totale:</div>
                          <div className="font-mono text-right font-bold text-red-700">{eurFull(p.valore)}</div>
                        </div>
                        {p.count > 0 && (
                          <div className="mt-2 pt-2 border-t border-slate-200 text-[10px] text-slate-500 italic">
                            Clicca la barra per il dettaglio →
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                <Bar
                  dataKey="valore"
                  name="Uscite programmate (IVA)"
                  cursor="pointer"
                  onClick={(entry) => {
                    if (entry?.count > 0) setSelectedBucket(entry.bucket);
                  }}
                  radius={[4, 4, 0, 0]}
                >
                  {chartData.map((d, i) => (
                    <Cell
                      key={i}
                      fill={d.is_current ? "#ea580c" : d.valore > 0 ? "#dc2626" : "#e2e8f0"}
                      data-testid={`bar-week-${i}`}
                    />
                  ))}
                  <LabelList
                    dataKey="valore"
                    position="top"
                    formatter={(v) => (v ? eur(v) : "")}
                    style={{ fontSize: 11, fontWeight: 700, fill: "#1e293b" }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        {maxVal > 0 && (
          <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 bg-orange-600 inline-block rounded-sm" /> Settimana in corso
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 bg-red-600 inline-block rounded-sm" /> Settimane future
            </span>
          </div>
        )}
      </div>

      {/* Tabella riepilogo settimanale */}
      {chartData.length > 0 && (
        <div className="bg-white border border-slate-200">
          <div className="p-4 border-b border-slate-200">
            <div className="overline">Dettaglio settimane</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Settimana</th>
                <th className="text-right p-3">Rate</th>
                <th className="text-right p-3">Imponibile</th>
                <th className="text-right p-3">IVA</th>
                <th className="text-right p-3">Totale</th>
                <th className="text-right p-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {data?.buckets?.map((b) => (
                <tr key={b.week_index} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-3">
                    <div className="font-mono font-bold text-slate-900">{b.week_label}</div>
                    <div className="text-[10px] text-slate-500">
                      {fmtDate(b.week_start)} → {fmtDate(b.week_end)}
                      {b.is_current && (
                        <span className="ml-2 text-orange-600 font-bold uppercase">In corso</span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-right font-mono">{b.count}</td>
                  <td className="p-3 text-right font-mono">{eurFull(b.totale_imponibile)}</td>
                  <td className="p-3 text-right font-mono text-slate-500">{eurFull(b.totale_iva)}</td>
                  <td className={`p-3 text-right font-mono font-bold ${b.count ? "text-red-700" : "text-slate-400"}`}>
                    {eurFull(b.totale_ivato)}
                  </td>
                  <td className="p-3 text-right">
                    {b.count > 0 && (
                      <button
                        onClick={() => setSelectedBucket(b)}
                        data-testid={`btn-open-week-${b.week_index}`}
                        className="text-xs px-2 py-1 border border-slate-300 hover:bg-slate-100 rounded-sm font-semibold"
                      >
                        Dettaglio →
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal dettaglio settimana */}
      {selectedBucket && (
        <BucketDetailModal bucket={selectedBucket} onClose={() => setSelectedBucket(null)} />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, good, bad, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        {Icon && (
          <Icon
            className={`w-4 h-4 ${good ? "text-emerald-600" : bad ? "text-red-600" : "text-slate-400"}`}
          />
        )}
      </div>
      <div className="font-display font-black text-xl mt-1">{value}</div>
    </div>
  );
}

function BucketDetailModal({ bucket, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="modal-bucket-detail"
    >
      <div
        className="bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <div className="overline text-orange-600">
              Settimana {bucket.week_label}
              {bucket.is_current && <span className="ml-2 uppercase">· In corso</span>}
            </div>
            <h3 className="font-display font-black text-xl mt-0.5">
              {bucket.count} rat{bucket.count === 1 ? "a" : "e"} · {eurFull(bucket.totale_ivato)}
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {fmtDate(bucket.week_start)} → {fmtDate(bucket.week_end)}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="btn-close-modal"
            className="p-2 hover:bg-slate-200 rounded-sm"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-widest sticky top-0">
              <tr>
                <th className="text-left p-3">Scadenza</th>
                <th className="text-left p-3">Fornitore</th>
                <th className="text-left p-3">Descrizione</th>
                <th className="text-left p-3">Cantiere</th>
                <th className="text-left p-3">Stato</th>
                <th className="text-right p-3">Totale</th>
              </tr>
            </thead>
            <tbody>
              {bucket.rate.map((r) => {
                const st = STATO_STYLE[r.stato_calcolato] || STATO_STYLE.da_pagare;
                return (
                  <tr key={r.costo_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`rata-${r.costo_id}`}>
                    <td className="p-3 font-mono text-xs whitespace-nowrap">{fmtDate(r.data_scadenza)}</td>
                    <td className="p-3 font-semibold">{r.fornitore || "—"}</td>
                    <td className="p-3">
                      <div className="text-xs">{r.descrizione}</div>
                      {r.rata_numero && r.rata_totale && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          Rata {r.rata_numero}/{r.rata_totale}
                          {r.numero_richiesta && ` · Rich. ${r.numero_richiesta}`}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-xs text-slate-600">{r.cantiere_nome || "—"}</td>
                    <td className="p-3">
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 border rounded-sm ${st.cls}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono font-bold text-red-700">
                      {eurFull(r.totale_ivato)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-xs text-slate-600">
            Totale imponibile: <b>{eurFull(bucket.totale_imponibile)}</b> · IVA:{" "}
            <b>{eurFull(bucket.totale_iva)}</b>
          </div>
          <Link
            to="/contabilita?tab=rate"
            onClick={onClose}
            data-testid="btn-go-contabilita-rate"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs rounded-sm"
          >
            Vai a Contabilità → Rate <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
